import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import { RootState } from "../../../store/store";
import { useParticipants } from "../../fiscalUpgrade/CustomHooks/usePartcipants";
import { usePreviousMonths } from "../../fiscalUpgrade/CustomHooks/usePreviousMonths";
import { downloadParticipantMonthlyStatement } from "../../fiscalUpgrade/services/MonthlyCalculations/monthlyServices";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";

type PopUpProps = {
  readonly open: boolean;
  readonly handleClose: () => void;
};

export default function MonthlyReport(props: PopUpProps) {
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const { open, handleClose } = props;
  const handleDialogClose = () => {
    handleClose();
    restAll();
  };
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const User = useSelector((state: RootState) => state.auth);
  const [participant, setParticipant] = useState<any[]>([]);
  const [participantId, setParticipantId] = useState<string | null>(contactInformation?.participantAdminProfile ?? constants.OPTION_ALL);
  const [filterId, setFilterId] = useState<any | number | null>("-1");
  const { fetchParticipants } = useParticipants();
  const [option, setOption] = useState<number | null>(1);
  const [state, setState] = useState({
    sentNotification: false,
    downloads: false,
  });
  const [optionMonthError, setOptionMonthError] = useState<string | null>(null);
  const [checkboxError, setCheckboxError] = useState<string | null>(null);
  const [participantError, setParticipantError] = useState<string | null>(null);
  const handleChange = (event: any) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
    setCheckboxError("");
  };

  const formatMonthYear = (value: string) => {
    if (!value) return "";
    const d = new Date(value);
    const month = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
    const year = d.getFullYear();
    return `${month}, ${year}`;
  };
  const {
    montlyEndDateList,
    monthEndDate,
    fetchPreviousMonths,
    setMonthEndDate,
  } = usePreviousMonths(
    User.clientId,
    constants.PARTICIPANT_MONTHLY_LIST_NUMBER
  );
  function restAll() {
    setParticipantError(null);
    setOptionMonthError(null);
    setMonthEndDate(monthEndDate);
    setParticipantId(constants.OPTION_ALL);
    setFilterId("-1");
    setOption(1);
    setState({
      sentNotification: false,
      downloads: false,
    });
    setCheckboxError(null);
  }

  useEffect(() => {
    if (open) {
      fetchParticipants();
      fetchPreviousMonths();
    }
  }, [open]);

  useEffect(() => {
    if (User?.participantArray) {
      const updatedParticipants = [
        { participantName: constants.OPTION_ALL, participantId: -1 },
        ...User.participantArray,
      ];
      setParticipant(updatedParticipants);
    }
  }, [User?.participantArray]);
  useEffect(() => {
    const newId = contactInformation?.participantAdminProfile ?? constants.OPTION_ALL;
    if (participantId !== newId) {
      setParticipantId(newId);
    }
  }, [contactInformation?.participantAdminProfile]);

  const handlePartcipantChange = (value: any) => {
    if (value === null || value === undefined) {
      setParticipantId(null);
      setFilterId(null);
    } else {
      if (value === constants.OPTION_ALL) {
        setState({
          ...state,
          downloads: false,
        });
      }
      setParticipantId(value);
      const findName = participant?.find(
        (item: any) => item.participantName === value
      );
      if (findName?.participantId) {
        setFilterId(findName?.participantId);
      }
      setParticipantError("");
    }
  };

  async function handleGenerate() {
    const formattedToDate =
      monthEndDate === null ? null : dayjs(monthEndDate).format("YYYY-MM-DD");
    const optionBasedToDate =
      option === 2 && monthEndDate
        ? new Date(monthEndDate).toISOString().split("T")[0]
        : formattedToDate;
    const participantId = filterId > 0 ? filterId : constants.OPTION_ALL;
    const notification = state.sentNotification ? constants.IS_ACTIVE_YES : constants.IS_ACTIVE_NO;
    const download = state.downloads ? constants.IS_ACTIVE_YES : constants.IS_ACTIVE_NO;
    try {
      const response = await downloadParticipantMonthlyStatement(optionBasedToDate, participantId, notification, download, User.clientId);
      if (response && response.status === constants.STATUS_CODES.SUCCESS) {
        if (response.data.data === null) {
          if ((download === constants.IS_ACTIVE_NO && participantId > 0) || participantId == constants.OPTION_ALL) {
            notifyUser(
              response?.data?.message ?? translate("apiRequestFailed"),
              "success",
              translate("monthlyStatement")
            );
          } else {
            notifyUser(
              response?.data?.message ?? translate("apiRequestFailed"),
              "error",
              translate("monthlyStatement")
            );
          }
          handleDialogClose();
          return;
        }

        if (response?.data?.data?.fileContent) {
          const base64String = response?.data?.data?.fileContent;
          const byteCharacters = atob(base64String);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf", });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          let fileName = "ClientStatement.pdf";
          if (participantId && optionBasedToDate) {
            fileName = `ClientStatement-${participantId}-${optionBasedToDate}.pdf`;
          }
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notifyUser(response.data?.message, "success", translate("monthlyStatement"));
          handleDialogClose();
          setParticipantId(contactInformation?.participantAdminProfile);
          return;
        } else {
          notifyUser(response?.data?.message, "error", translate("monthlyStatement"));
          handleDialogClose();
        }
      } else {
        notifyUser(response?.response?.data?.message, "error", translate("monthlyStatement"));
        handleDialogClose();
      }
    } catch (error: any) {
      notifyUser(error.response?.data?.message ?? translate("apiRequestFailed"), "error", translate("monthlyStatement"));
      handleDialogClose();
    }
  }
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      sx={{
        maxWidth: "500px",
        margin: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        "& .MuiPaper-root": { borderRadius: "12px", background: "white" },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#059ca9",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 10px",
        }}
      >
        {translate("monthlyStatement")}
        <IconButton
          onClick={handleDialogClose}
          sx={{ color: "white", padding: "0px" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: "20px" }}>
        <Grid container columnSpacing={2}>
          <Grid size={{ xs: 12 }}>
            <CustomAutocomplete

              options={participant}
              labelText={translate("participants")}
              placeholder={translate("select")}
              getOptionLabel={(option) => option.participantName || ""}
              isOptionEqualToValue={(option, value) =>
                option.participantName === value.participantName
              }
              value={
                participant.find((p) => p.participantName === participantId) ||
                null
              }
              onChange={(event, newValue) =>
                handlePartcipantChange(newValue?.participantName || null)
              }
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              error={Boolean(participantError)}
            />
            {participantError && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {participantError}
              </FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CustomAutocomplete
              labelText={translate("clientMonth")}
              options={montlyEndDateList}
              getOptionLabel={(option: any) =>
                option?.value ? formatMonthYear(option.value) : ""
              }
              value={
                montlyEndDateList.find(
                  (d: any) => d.value === monthEndDate
                ) ?? null
              }
              onChange={(event, newValue) => {
                setMonthEndDate(newValue?.value ?? "");
                setOptionMonthError("");
              }}
              error={Boolean(optionMonthError)}
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              placeholder={translate("select")}
            />
            {optionMonthError && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {optionMonthError}
              </FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl component="fieldset">
              <FormGroup aria-label="position" row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.sentNotification}
                      onChange={handleChange}
                      name="sentNotification"
                    />
                  }

                  label={translate("sentNotification")}
                  labelPlacement="end"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.downloads}
                      onChange={handleChange}
                      name="downloads"
                    />
                  }
                  disabled={participantId === constants.OPTION_ALL}
                  label={translate("download")}
                  labelPlacement="end"
                />
              </FormGroup>
            </FormControl>
            {checkboxError && (
              <FormHelperText
                style={{
                  color: "#d32f2f",
                  fontSize: "15px",
                  marginTop: "-9px",
                }}
              >
                {checkboxError}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <ContainedButton onClick={handleGenerate} disabled={participantId === "" || participantId === constants.ADMIN_VIEW}>
          {translate("submit")}
        </ContainedButton>
        <OutlinedButton onClick={handleDialogClose}>
          {translate("cancel")}
        </OutlinedButton>
      </DialogActions>
    </Dialog>
  );
}